import { inject } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { AuthService } from './auth.service';
import { LoggerService } from '../logging/logger.service';
import { environment } from '../../../environments/environment';

/**
 * Handles the Entra ID redirect on app startup.
 *
 * Entra sends the user back to the app ROOT (the only redirect URI registered on
 * the app registration) with `?code=...&state=...`. Doing the exchange here rather
 * than in a route guard means the query string is consumed before any `redirectTo`
 * can drop it.
 *
 * Two pieces of ordering matter, because Angular awaits app initializers with
 * Promise.all rather than sequentially:
 *  - the params are read and stripped synchronously, since the router's initial
 *    navigation is queued as a microtask and would otherwise bake `?code=` into
 *    the URL it serializes when navigation completes;
 *  - the exchange promise is handed to AuthService so authGuard can await it
 *    instead of redirecting mid-flight.
 */
export function initializeAuth(): Promise<void> {
  const auth = inject(AuthService);
  const logger = inject(LoggerService);

  if (environment.bypassAuth) {
    auth.initLocalBypass();
    return Promise.resolve();
  }

  const params = new URLSearchParams(window.location.search);
  stripAuthParams();

  const error = params.get('error');
  if (error) {
    logger.error('EntraAuthorizeError', new Error(error), {
      description: params.get('error_description'),
    });
    auth.markLoginFailed();
    return Promise.resolve();
  }

  const code = params.get('code');
  if (!code) {
    return Promise.resolve();
  }

  const done = exchangeCode(auth, logger, code, params.get('state'));
  auth.setReady(done);
  return done;
}

async function exchangeCode(
  auth: AuthService,
  logger: LoggerService,
  code: string,
  state: string | null
): Promise<void> {
  try {
    await firstValueFrom(auth.exchangeCode(code, state));
  } catch (err) {
    logger.error('CodeExchangeFailed', err);
    auth.markLoginFailed();
  }
}

function stripAuthParams(): void {
  const url = new URL(window.location.href);
  for (const key of ['code', 'state', 'session_state', 'error', 'error_description', 'error_uri']) {
    url.searchParams.delete(key);
  }
  window.history.replaceState({}, '', `${url.pathname}${url.search}${url.hash}`);
}
